import { ChangeEventHandler, FormEventHandler, useState } from "react";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { confirmSignIn, fetchUserAttributes, signIn } from "aws-amplify/auth";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { actions } from "@/redux/reducers/User";
// Helpers
import { getLoginUser, login } from "@/Auth/api/Login";
import { errorLogger } from "@/Common/helpers/logger";
// Components
import LoginContainer from "@/Auth/Layouts/LoginContainer";
import DelayedButton from "../components/DelayedButton";
import StartAdornment from "../components/StartAdornment";
// MUI
import { Button, Container, Stack, Typography, TextField, CircularProgress } from "@mui/material";
// Images
import CheckBadge from "@/Auth/assets/icons/checkbadge.svg";

import { tenant } from "@/Constants/globalConfig";

const DEFAULT_RESEND_DELAY = 30;
const INITIAL_STATE = {
	username: "",
	password: "",
	password2: "",
	passwordStrength: "",
	code: "",
	someEmptyField: true,
	remember: false,
	showPassword: false,
	showPassword2: false,
	error: false,
	visible: false,
	isLoading: false,
	message: "The username or password you entered is incorrect.",
};

type LoggedUser = {
	username: string;
	password: string;
};

export default function MultiFactorAuthentication() {
	const dispatch = useDispatch();

	const queryString = useLocation() as Location & { loggedUser: LoggedUser };
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const [locationId, setLocationId] = useState(null);
	const [userId, setUserId] = useState<string | null>(null);
	const [resendDisabled, setResendDisabled] = useState(true);
	const [state, setState] = useState(INITIAL_STATE);

	const { isLoading: isLoadingUser } = useQuery({
		queryKey: ["userToLogin", userId],
		queryFn: async () => {
			const user = await getLoginUser(tenant, userId);
			setLocationId(user?.preferences?.landingLocation || user.locationId);
			queryClient.removeQueries({ queryKey: ["userToLogin"] });
			return user;
		},
		enabled: !!userId,
	});

	const { isLoading: isLoadingLogin } = useQuery({
		queryKey: ["loggedUser"],
		queryFn: async () => {
			const logged = await login(locationId, queryString?.state.username);

			if (logged) {
				setState((oldValues) => ({
					...oldValues,
					error: false,
				}));

				// Set User in redux & localStorage
				dispatch(actions.SET_USER({ user: logged }));

				// Search preferences
				let landingPage = "/dashboard";

				if (logged.preferences?.landingPage) {
					landingPage = logged.preferences.landingPage;
				}

				navigate(landingPage);
			} else {
				setState((oldValues) => ({
					...oldValues,
					message: "The username or password you entered is incorrect.",
					error: true,
				}));
			}

			return logged;
		},
		enabled: !!locationId,
	});

	const handleChangeCode: ChangeEventHandler<HTMLInputElement> = (e) => {
		let someEmptyField = true;
		if (e.target.value) {
			someEmptyField = false;
		}
		setState((oldValues) => ({
			...oldValues,
			someEmptyField,
			code: e.target.value,
		}));
	};

	const handleResendCode = async () => {
		try {
			await signIn({
				username: queryString?.state.username,
				password: queryString?.state.password,
				options: {
					authFlowType: "USER_SRP_AUTH",
				},
			});
		} catch (error) {
			setUserId(null);
		}
	};

	const handleSubmit: FormEventHandler = (e): void => {
		e.preventDefault();

		setState((oldValues) => ({
			...oldValues,
			isLoading: true,
		}));

		(async () => {
			try {
				if (!queryString?.state?.signInResult) {
					errorLogger("No result found in queryString");
				}

				const res = await confirmSignIn({ challengeResponse: state.code });
				const { isSignedIn, nextStep } = res;
				if (isSignedIn && nextStep.signInStep === "DONE") {
					setState((oldValues) => ({
						...oldValues,
						username: queryString?.state.username,
						isLoading: false,
					}));

					//get userAttributes
					const userAttributes = await fetchUserAttributes();
					setUserId(userAttributes?.["custom:_id"] ?? null);
				}
			} catch (error) {
				setState((oldValues) => ({
					...oldValues,
					error: true,
					message: "Invalid Code",
					isLoading: false,
				}));
			}
		})();
	};

	const isLoading = isLoadingUser || isLoadingLogin;
	const isState = state.isLoading || state.someEmptyField;
	const disabled = isState || isLoading || false;

	return (
		<LoginContainer>
			<form
				className={clsx({
					"form-login form-custom": true,
					"form-error": state.error,
				})}
				noValidate
				onSubmit={handleSubmit}
			>
				<Stack className="mx-[13px]">
					<Container className="px-0 mt-[6px]">
						<Typography className="text-regular mb-[13px] ml-[15px] font-medium">
							An authorization code was sent to your phone
						</Typography>
						<TextField
							placeholder="Enter Code"
							variant="outlined"
							fullWidth
							focused={true}
							required={true}
							className="mb-[20px] mt-0 px-0"
							value={state.code}
							disabled={state.isLoading || false}
							onChange={handleChangeCode}
							InputProps={{
								className: "text-regular h-[37px] pl-[14px] pb-[4px] mt-0 rounded-[6px]",
								startAdornment: <StartAdornment src={CheckBadge} alt="Check bagde icon" />,
							}}
						/>

						<DelayedButton
							countFrom={DEFAULT_RESEND_DELAY}
							clickHandler={handleResendCode}
							text="Resend"
							stateHandler={setResendDisabled}
							className={clsx({
								"ml-[15px] mb-[15px] mt-0 capitalize text-regular p-0 !bg-transparent hover:!bg-transparent": true,
								"!text-[#019BD9] underline": !resendDisabled,
								"!text-[#CCC]": resendDisabled,
							})}
						/>
					</Container>
				</Stack>

				<Stack className={clsx({ "p-0 min-h-[40px] mx-[13px]": true, hidden: !state.error })}>
					<Typography className={clsx({ "alert-message": true, block: state.error, hidden: !state.error })}>
						{state.message}
					</Typography>
				</Stack>

				<Stack
					direction="row"
					justifyContent="center"
					alignContent="center"
					className={clsx({ "mx-[13px]": true, "mt-[5px]": state.error, "mt-[34px]": !state.error })}
				>
					<Button variant="contained" className="btn-submit" type="submit" disabled={disabled} disableElevation>
						{state.isLoading || isLoading ? <CircularProgress color="inherit" size={25} /> : "Verify Code"}
					</Button>
				</Stack>
			</form>
		</LoginContainer>
	);
}
